import type { LayerCheckState, LayerTreeNode, LayerVisibilityPatch } from "../layers";

/**
 * Tree helpers for the layers panel.
 *
 * All derived, nothing stored: a container's checkbox state is computed from the
 * visibility of the leaves beneath it, so the consumer stays the single owner of
 * layer state and the panel cannot drift out of sync with the map.
 *
 * Every mutation is returned as a patch — an explicit target state per layer —
 * rather than applied here.
 */

export type IsVisible = (layerId: string) => boolean;

/** True when the node's children are alternatives rather than additions. */
export function isSingleSelect(node: LayerTreeNode): boolean {
  return node.selection?.mode === "single";
}

/** Every `layerId` at or beneath `node`, depth-first, in declaration order. */
export function collectLeafLayerIds(node: LayerTreeNode): string[] {
  const ids: string[] = [];

  const walk = (current: LayerTreeNode) => {
    if (current.layerId) ids.push(current.layerId);
    for (const child of current.children ?? []) walk(child);
  };

  walk(node);
  return ids;
}

/**
 * The node's checkbox state, given which layers are currently visible.
 *
 * A container with no leaves anywhere beneath it reads as `unchecked` — there is
 * nothing it could show, so claiming otherwise would be a lie the user can't act
 * on. A `single` node never reports `indeterminate`: showing one of its children
 * is the fully-on state, not a partial one.
 */
export function resolveCheckState(node: LayerTreeNode, isVisible: IsVisible): LayerCheckState {
  const leaves = collectLeafLayerIds(node);
  if (leaves.length === 0) return "unchecked";

  let visible = 0;
  for (const id of leaves) if (isVisible(id)) visible++;

  if (visible === 0) return "unchecked";
  if (isSingleSelect(node)) return "checked";
  if (visible === leaves.length) return "checked";
  return "indeterminate";
}

/** The child of a `single` node that is currently showing, if any. */
export function resolveSelectedChild(node: LayerTreeNode, isVisible: IsVisible): LayerTreeNode | undefined {
  return node.children?.find((child) => collectLeafLayerIds(child).some(isVisible));
}

const patchOf = (layerIds: string[], visible: boolean): LayerVisibilityPatch =>
  Object.fromEntries(layerIds.map((id) => [id, visible]));

/**
 * What clicking this row's checkbox should do.
 *
 * Partially-visible containers turn fully on rather than off: the click is a
 * request to see the group, and "some on" → "all on" is the reading that matches
 * a checkbox that isn't showing as checked.
 *
 * A `single` node turns on by showing its first child, not all of them — turning
 * on every alternative at once is exactly what `single` forbids.
 */
export function resolveToggle(node: LayerTreeNode, isVisible: IsVisible): LayerVisibilityPatch {
  const leaves = collectLeafLayerIds(node);
  const turningOn = resolveCheckState(node, isVisible) !== "checked";

  if (!turningOn) return patchOf(leaves, false);

  if (isSingleSelect(node)) {
    const first = node.children?.[0];
    const chosen = first ? collectLeafLayerIds(first) : [];
    // Everything off, then the first alternative on, in one patch.
    return { ...patchOf(leaves, false), ...patchOf(chosen, true) };
  }

  return patchOf(leaves, true);
}

/**
 * What choosing `childId` inside a `single` node should do: that child on, its
 * siblings off. Re-choosing the current selection is a no-op unless the node
 * allows clearing, where it turns the choice off.
 */
export function resolveSelect(node: LayerTreeNode, childId: string, isVisible: IsVisible): LayerVisibilityPatch {
  const child = node.children?.find((candidate) => candidate.id === childId);
  if (!child) return {};

  const siblings = collectLeafLayerIds(node);
  const chosen = collectLeafLayerIds(child);
  const alreadyOn = chosen.length > 0 && chosen.every(isVisible);

  if (alreadyOn) {
    return node.selection?.mode === "single" && node.selection.allowNone ? patchOf(chosen, false) : {};
  }

  return { ...patchOf(siblings, false), ...patchOf(chosen, true) };
}

/** Depth-first lookup by node id. */
export function findNode(nodes: LayerTreeNode[], id: string): LayerTreeNode | undefined {
  for (const node of nodes) {
    if (node.id === id) return node;
    const found = findNode(node.children ?? [], id);
    if (found) return found;
  }
  return undefined;
}

/** The node whose `children` contain `id`, or undefined for a root-level node. */
export function findParent(nodes: LayerTreeNode[], id: string): LayerTreeNode | undefined {
  for (const node of nodes) {
    if (node.children?.some((child) => child.id === id)) return node;
    const found = findParent(node.children ?? [], id);
    if (found) return found;
  }
  return undefined;
}

/**
 * Ids that collide within their own namespace, which would make lookups and pane
 * keys resolve to the wrong row. Empty when the tree is well-formed.
 *
 * Node ids and layer ids are tracked separately: a leaf naming its layer after
 * itself (`{ id: "sites", layerId: "sites" }`) is perfectly valid and must not be
 * reported.
 */
export function findDuplicateIds(nodes: LayerTreeNode[]): string[] {
  const seenNodeIds = new Set<string>();
  const seenLayerIds = new Set<string>();
  const duplicates = new Set<string>();

  const track = (key: string | undefined, seen: Set<string>) => {
    if (!key) return;
    if (seen.has(key)) duplicates.add(key);
    else seen.add(key);
  };

  const walk = (node: LayerTreeNode) => {
    track(node.id, seenNodeIds);
    track(node.layerId, seenLayerIds);
    for (const child of node.children ?? []) walk(child);
  };

  for (const node of nodes) walk(node);
  return [...duplicates];
}
